<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { $, defineSchema } from '@bagelink/vue'
const schema = defineSchema({
	anchor: $.text('Section anchor', { default: 'Register' }),
	mode: $.select('Mode', [
		{ label: 'Registration and support', value: 'registration' },
		{ label: 'Donation only', value: 'donation' },
		{ label: 'Unable to attend / donate', value: 'unable-to-attend' },
		{ label: 'Young Leadership', value: 'young-leadership' },
		{ label: 'After-party', value: 'after-party' },
	], { default: 'registration' }),
	displayMode: $.select('Display mode', [
		{ label: 'Active form', value: 'active' },
		{ label: 'View options only', value: 'view-only' },
	], { default: 'active' }),
	eyebrow: $.text('Eyebrow'),
	title: $.text('Heading'),
	specialHeading: $.checkbox('Use special heading font', { default: true }),
	intro: $.text('Introduction', { multiline: true }),
	optionsHeading: $.text('Options heading'),
	optionLayout: $.select('Option presentation', [
		{ label: 'Clickable cards', value: 'cards' },
		{ label: 'Compact dropdown', value: 'select' },
	], { default: 'cards' }).if('displayMode eq "active"'),
	showUnableToAttend: $.checkbox('Show “Can’t attend” option', { default: true }).if('mode eq "registration" and displayMode eq "active"'),
	unableToAttendHeading: $.text('Can’t attend heading', { default: 'Can’t attend' }).if('showUnableToAttend eq true and mode eq "registration" and displayMode eq "active"'),
	unableToAttendText: $.text('Can’t attend description', {
		multiline: true,
		default: 'I can’t attend the event, but would still like to make a donation.',
	}).if('showUnableToAttend eq true and mode eq "registration" and displayMode eq "active"'),
	donationAmountLabel: $.text('Open donation field label', { default: 'Donation amount' }).if('mode eq "donation" or mode eq "unable-to-attend" or showUnableToAttend eq true'),
	tierLabel: $.text('Tier field label', { default: 'Registration or support level' }).if('displayMode eq "active"'),
	quantityLabel: $.text('Quantity field label', { default: 'Quantity' }).if('displayMode eq "active"'),
	maxQuantity: $.number('Maximum quantity', { default: 10, min: 1 }).if('displayMode eq "active"'),
	showGuests: $.checkbox('Collect attendee details', { default: true }).if('mode neq "donation" and mode neq "unable-to-attend" and displayMode eq "active"'),
	maxGuests: $.number('Maximum additional attendees', { default: 10, min: 0 }).if('showGuests eq true and displayMode eq "active"'),
	guestHelpText: $.text('Attendee help text', {
		multiline: true,
		default: 'Add each attendee separately. Email and phone are optional.',
	}).if('showGuests eq true and displayMode eq "active"'),
	showAdditionalDonation: $.checkbox('Allow an additional donation', { default: true }).if('displayMode eq "active"'),
	collectAddress: $.checkbox('Collect billing address', { default: true }).if('displayMode eq "active"'),
	showAnonymous: $.checkbox('Allow anonymous gifts', { default: true }).if('displayMode eq "active"'),
	showNewsletter: $.checkbox('Show newsletter opt-in', { default: true }).if('displayMode eq "active"'),
	showJournalAdHelp: $.checkbox('Show journal ad instructions', { default: false }),
	journalAdText: $.text('Journal ad instructions', {
		multiline: true,
		default: 'Journal ad copy and artwork are collected separately after checkout.',
	}).if('showJournalAdHelp eq true'),
	journalAdUrl: $.url('Journal ad submission URL').if('showJournalAdHelp eq true'),
	showBillMeLater: $.checkbox('Show bill-me-later contact option', { default: false }),
	billMeLaterText: $.text('Bill-me-later explanation', {
		multiline: true,
		default: 'To make a pledge or request an invoice, contact Shalva. This form only processes secure card payments.',
	}).if('showBillMeLater eq true'),
	deadlineText: $.text('Deadline or availability notice', { multiline: true }),
	helpText: $.text('Help text', { multiline: true }),
	legalText: $.text('Tax / legal text', { multiline: true }),
	contactName: $.text('Contact person'),
	contactEmail: $.text('Contact email'),
	contactPhone: $.text('Contact phone'),
	contactUrl: $.url('Contact URL', { default: 'https://www.shalva.org/contact-us/' }).if('displayMode eq "active"'),
	successPath: $.text('Success page path', { default: '/thank-you' }).if('displayMode eq "active"'),
	surface: $.select('Background', [
		{ label: 'Page background', value: 'page' },
		{ label: 'White surface', value: 'surface' },
		{ label: 'Image', value: 'image' },
	], { default: 'page' }),
	backgroundImage: $.upload('Background image').if('surface eq "image"'),
	backgroundFit: $.select('Background image fit', [
		{ label: 'Cover', value: 'cover' },
		{ label: 'Contain', value: 'contain' },
		{ label: 'Fill', value: 'fill' },
	], { default: 'cover' }).if('surface eq "image" and backgroundImage pr'),
	dimBackground: $.checkbox('Dim background', { default: false }).if('surface eq "image" and backgroundImage pr'),
})
export const EventRegistration = defineBlock({
	name: 'EventRegistration',
	label: 'Registration and payment',
	icon: 'how_to_reg',
	thumbnail: '/block-thumbs/EventRegistration.svg',
	category: 'Event',
	order: 100,
	schema,
})
</script>
<script setup lang="ts">
import { computed } from 'vue'

import { pathKeyToURL } from '@bagelink/vue'
import EventRegistrationFlow from '../EventRegistrationFlow.vue'



const props = defineProps<{
	anchor?: string
	mode?: 'registration' | 'donation' | 'unable-to-attend' | 'young-leadership' | 'after-party'
	displayMode?: 'active' | 'view-only'
	eyebrow?: string
	title?: string
	specialHeading?: boolean
	intro?: string
	optionsHeading?: string
	optionLayout?: 'cards' | 'select'
	showUnableToAttend?: boolean
	unableToAttendHeading?: string
	unableToAttendText?: string
	donationAmountLabel?: string
	tierLabel?: string
	quantityLabel?: string
	maxQuantity?: number
	showGuests?: boolean
	maxGuests?: number
	guestHelpText?: string
	showAdditionalDonation?: boolean
	collectAddress?: boolean
	showAnonymous?: boolean
	showNewsletter?: boolean
	showJournalAdHelp?: boolean
	journalAdText?: string
	journalAdUrl?: string
	showBillMeLater?: boolean
	billMeLaterText?: string
	deadlineText?: string
	helpText?: string
	legalText?: string
	contactName?: string
	contactEmail?: string
	contactPhone?: string
	contactUrl?: string
	successPath?: string
	surface?: 'page' | 'surface' | 'image'
	backgroundImage?: string
	backgroundFit?: 'cover' | 'contain' | 'fill'
	dimBackground?: boolean
}>()
const sectionStyle = computed(() => props.surface === 'image' && props.backgroundImage
	? {
			backgroundImage: `${props.dimBackground ? 'linear-gradient(#0006, #0006), ' : ''}url("${pathKeyToURL(props.backgroundImage) || props.backgroundImage}")`,
			backgroundPosition: 'center',
			backgroundRepeat: 'no-repeat',
			backgroundSize: props.backgroundFit === 'fill' ? '100% 100%' : (props.backgroundFit ?? 'cover'),
		}
	: undefined)
</script>

<template>
	<section
		:id="anchor"
		class="flow-shell m_px-1"
		:class="surface === 'surface' ? 'bg-box radius-2 px-2 m_px-1' : surface === 'image' ? 'color-white keep-white px-2 m_px-1' : ''"
		:style="sectionStyle"
	>
		<EventRegistrationFlow
			:mode="mode"
			:view-only="displayMode === 'view-only'"
			:eyebrow="eyebrow"
			:title="title"
			:special-heading="specialHeading"
			:intro="intro"
			:options-heading="optionsHeading"
			:option-layout="optionLayout"
			:show-unable-to-attend="showUnableToAttend"
			:unable-to-attend-heading="unableToAttendHeading"
			:unable-to-attend-text="unableToAttendText"
			:donation-amount-label="donationAmountLabel"
			:tier-label="tierLabel"
			:quantity-label="quantityLabel"
			:max-quantity="maxQuantity"
			:show-guests="showGuests && mode !== 'donation' && mode !== 'unable-to-attend'"
			:max-guests="maxGuests"
			:guest-help-text="guestHelpText"
			:show-additional-donation="showAdditionalDonation"
			:collect-address="collectAddress"
			:show-anonymous="showAnonymous"
			:show-newsletter="showNewsletter"
			:show-journal-ad-help="showJournalAdHelp"
			:journal-ad-text="journalAdText"
			:journal-ad-url="journalAdUrl"
			:show-bill-me-later="showBillMeLater"
			:bill-me-later-text="billMeLaterText"
			:deadline-text="deadlineText"
			:help-text="helpText"
			:legal-text="legalText"
			:contact-name="contactName"
			:contact-email="contactEmail"
			:contact-phone="contactPhone"
			:contact-url="contactUrl"
			:success-path="successPath"
		/>
	</section>
</template>
